import { PaginatedResponse } from "@/shared/http";

import { isBoardDraftId } from "./boardDraftId";
import {
  type BoardDraftStore,
  type CreateBoardDraftRequest,
} from "./BoardDraftStore";
import {
  boardDefinitionTitle,
  type Board,
  type BoardDraft,
  type BoardRecord,
  type CreateBoardRequest,
  type UpdateBoardRequest,
  type UpdateBoardAccessRequest,
} from "./BoardRecord";
import {
  type BoardRequestOptions,
  type PlatformBoardStore,
} from "./PlatformBoardStore";

/** What the platform store and the draft store both do with one board. */
interface BoardStore {
  delete(
    boardId: string,
    orgId: string,
    options?: BoardRequestOptions,
  ): Promise<void> | void;
  get(
    boardId: string,
    orgId: string,
    options?: BoardRequestOptions,
  ): Promise<Board> | Board;
  update(
    boardId: string,
    request: UpdateBoardRequest,
    orgId: string,
    options?: BoardRequestOptions,
  ): Promise<Board> | Board;
}

/**
 * Every board a person can open, wherever it is kept: stored boards on the
 * platform, and the person's drafts in this browser.
 *
 * A board's id says which of the two keeps it (`isBoardDraftId`), and this
 * class is the only code that asks. A caller reads, writes and deletes a draft
 * with the methods and the id it uses for a stored board.
 */
export class BoardService {
  #platform: PlatformBoardStore;
  #drafts: BoardDraftStore;

  constructor(platform: PlatformBoardStore, drafts: BoardDraftStore) {
    this.#platform = platform;
    this.#drafts = drafts;
  }

  /** Stores a new board on the platform. */
  public async createBoard(
    request: CreateBoardRequest,
    orgId: string,
    options?: BoardRequestOptions,
  ): Promise<BoardRecord> {
    return this.#platform.create(request, orgId, options);
  }

  /** Starts a draft in this browser. Nothing is sent to the platform. */
  public createBoardDraft(
    request: CreateBoardDraftRequest,
    orgId: string,
  ): Promise<BoardDraft> {
    return Promise.resolve(this.#drafts.create(request, orgId));
  }

  public async deleteBoard(
    boardId: string,
    orgId: string,
    options?: BoardRequestOptions,
  ): Promise<void> {
    await this.#storeOf(boardId).delete(boardId, orgId, options);
  }

  /**
   * One board by id. A stored board that does not exist and another person's
   * personal board both come back as 404, so a read cannot tell a caller that
   * a board they may not open exists. A draft this browser does not hold for
   * the person fails the same way.
   */
  public async getBoardById(
    boardId: string,
    orgId: string,
    options?: BoardRequestOptions,
  ): Promise<Board> {
    return this.#storeOf(boardId).get(boardId, orgId, options);
  }

  /**
   * One page of the boards the caller can open in `orgId`. The first page
   * starts with the caller's drafts, the most recently written first. The
   * stored boards follow, ordered by name: the organization's shared boards
   * and the caller's own personal ones, leaving out the personal boards of
   * teammates, which the caller could not open.
   */
  public async getBoardsForOrg(
    orgId: string,
    options?: BoardRequestOptions & Partial<{ pageToken: string }>,
  ): Promise<PaginatedResponse<Board>> {
    const page = await this.#platform.list(orgId, options);
    if (options?.pageToken !== undefined) {
      return page;
    }
    return { ...page, items: [...this.#drafts.list(orgId), ...page.items] };
  }

  /**
   * Saves a draft: stores its definition on the platform as a new board named
   * `name`, then removes the draft from this browser. A definition with a
   * title takes `name` as its title, because the platform keeps the two equal.
   *
   * When the platform refuses, as it does for a name the person already uses,
   * this throws what the platform answered and the draft stays.
   */
  public async saveBoardDraft(
    boardId: string,
    name: string,
    orgId: string,
  ): Promise<BoardRecord> {
    const draft = this.#drafts.get(boardId, orgId);
    const definition = draft.board_definition;
    const record = await this.#platform.create(
      {
        name,
        board_definition:
          boardDefinitionTitle(definition) === null
            ? definition
            : { ...definition, title: name },
      },
      orgId,
    );
    this.#drafts.delete(boardId, orgId);
    return record;
  }

  /**
   * Renames a board, or replaces its definition. Build `request` with
   * `updateBoardRequestFor`.
   *
   * A request's base revision is compared with the board's revision as kept:
   * by the server under a row lock, or against the draft in this browser. On a
   * mismatch the request fails with `RobotoBoardRevisionConflictException`,
   * which carries the board as kept, so the caller can offer a resolution
   * without another request.
   */
  public async updateBoard(
    boardId: string,
    request: UpdateBoardRequest,
    orgId: string,
    options?: BoardRequestOptions,
  ): Promise<Board> {
    return this.#storeOf(boardId).update(boardId, request, orgId, options);
  }

  /** Shares a stored board with its organization. */
  public async updateBoardAccess(
    boardId: string,
    request: UpdateBoardAccessRequest,
    orgId: string,
  ): Promise<BoardRecord> {
    return this.#platform.updateAccess(boardId, request, orgId);
  }

  #storeOf(boardId: string): BoardStore {
    return isBoardDraftId(boardId) ? this.#drafts : this.#platform;
  }
}
